












































import Vue, { VueConstructor } from 'vue';
import { Component, Watch } from 'vue-property-decorator';

import { ProductsState } from '@/store';
import { Product } from '@/dto';
import {
    CONTRACTS,
    WindowsServiceContract,
    DialogButton,
} from '@/core/contracts';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import { TilesService } from '@/core/services/tilesService';
import { Node, Joint } from '@/core/helpers/tree';
import { guid } from '@/core/helpers/utils';
import {
    TilesContainer,
    TileData,
    ContainerData,
    Dialog,
    Direction,
    Box,
    Position,
} from './types';
import {
    insertToLargestTile,
    deleteTile,
    getBoxStyle,
    getCssText,
    updateSplitRatio,
    expandTile,
    insertToTile,
    createJoint,
    createTileData,
    boxesAreEqual,
} from './utils';
import { enumerateNodes, isJoint, isLeaf } from '@/core/helpers/tree';
import Splitter from './Splitter.vue';
import Modal from './Modal';
import ModalDialog from './ModalDialog.vue';
import { DnDSource, DnDTarget, IDnDData } from '@/core/components/dnd';

@Component({
    components: {
        Splitter,
        DnDSource,
        DnDTarget,
        Modal,
    },
})
export default class Tiles extends Vue implements TilesContainer {
    /**
     * Drag-n-drop operation data type key
     */
    public readonly DragDataType: string = 'TileData';

    public tiles: TileData[] = [];
    public joints: Array<Joint<TileData, ContainerData>> = [];
    public dialogs: Dialog[] = [];

    public droppedTileConst: TileData | null = null;
    public droppedTileDOM: Element | null = null;
    public targetTileDOM: Element | null = null;
    public targetTileDOM2: Element | null = null;
    public dragStartMosaic: boolean = false;
    public dragStartMosaic2: boolean = false;
    public dragStartMosaic3: boolean = false;
    public position: Position | null = null;
    public positionEquelsCss: Position | null = null;
    public currentTileKey: string = '';

    @Inject(CONTRACTS.WindowsService)
    private _windowsService?: WindowsServiceContract;

    private _tree: Node<TileData, ContainerData> | undefined;
    private lastSourceTile: TileData | null = null;
    private lastTargetTile: TileData | null = null;

    public mounted() {
        window.addEventListener('resize', this.windowResized);
    }

    public destroyed() {
        window.removeEventListener('resize', this.windowResized);
    }

    public get getBoxStyle() {
        return getBoxStyle;
    }

    private get TileSettings() {
        return (this._windowsService as TilesService).getTileSettings;
    }

    public get currentProduct() {
        return ProductsState.current;
    }

    public getLayout() {
        return this.tiles;
    }

    @Watch('TileSettings')
    public onTilesChange(newValue: TileData[]) {
        if (newValue) {
            const tiles: TileData[] = this.TileSettings;

            (this._windowsService as TilesService).restoreLayout(tiles);
        }
    }

    @Watch('currentProduct')
    public onProductFilterChanged(value: Product) {
        this.tiles.forEach(tile => {
            this.update(tile.name, tile.args, tile.key);
        });
    }

    public storeCurrentTile(key: string) {
        this.currentTileKey = key;
    }

    public created(): void {
        (this._windowsService as TilesService).setContainer(this);
    }

    public open(name: string, args?: any): string {
        const key = guid();

        let currentTile: TileData | null = null;
        if (this.currentTileKey !== '') {
            currentTile = this.tiles.find(r => r.key === this.currentTileKey)!;
        }

        const { root, modifiedTile, modifiedJoint } = insertToLargestTile(
            this._tree,
            { key, name, args },
            currentTile!
        );
        this._tree = root;
        this.tiles.push(modifiedTile!);

        if (modifiedJoint !== undefined) {
            this.joints.push(modifiedJoint);
        }

        if (this.tiles.length > 1) {
            const updatedKeys = updateSplitRatio(
                modifiedJoint!,
                modifiedJoint!.data!.ratio
            );
            this.fireResizeEventForKeys(updatedKeys);
        }

        return modifiedTile!.key;
    }

    public showModalComponent(
        component: Vue | VueConstructor<Vue>,
        args?: any
    ): Promise<any> {
        const dialog = {
            id: this.dialogs.length,
            component,
            args,
        } as Dialog;

        const promise = new Promise(resolve => (dialog.close = resolve));
        this.dialogs.push(dialog);
        return promise;
    }

    public showDialog(
        message?: string,
        buttons?: DialogButton[]
    ): Promise<any> {
        if (!buttons) {
            buttons = [{ id: 1, caption: 'OK' }];
        }

        return this.showModalComponent(ModalDialog, {
            message,
            buttons,
        });
    }

    public async restoreLayout(layout: TileData[]): Promise<void> {
        layout.forEach(tile => {
            if (!this.tiles.find(t => t.key === tile.key)) {
                const key = this.restoreTile(tile.name, tile.args, tile.key);
            }
        });
    }

    public onModalClose(dialog: Dialog) {
        this.dialogs = this.dialogs.filter(x => x !== dialog);
        const args: any = Array.prototype.slice.call(arguments, 1);
        dialog.close.apply(dialog, args);
    }

    public update(name: string, args?: any, id?: string): void {
        const tileIndex = id
            ? this.tiles.findIndex((x, index) => x.key === id)
            : this.tiles.findIndex((x, index) => x.name === name);

        if (tileIndex === -1) {
            this.open(name, args);
        } else {
            const box: Box = { ...this.tiles[tileIndex].box };
            this.reopen(name, args, box, id);
        }
    }

    /**
     * Handler that processes close event.
     * @param {string} id ID of tile to be closed
     */
    public close(id: string): boolean {
        const { root, modifiedTile, modifiedJoint } = deleteTile(
            this._tree,
            id
        );
        this._tree = root;

        if (modifiedTile !== undefined) {
            this.tiles = this.tiles.filter(x => x.key !== modifiedTile.key);
        }

        if (modifiedJoint !== undefined) {
            this.joints = this.joints.filter(x => x !== modifiedJoint);
        }

        if (this.tiles.length > 0) {
            this.windowResized();
        }

        return modifiedTile !== null;
    }

    public closeAll(): void {
        if (this.tiles.length > 0) {
            this.close(this.tiles[0].key);
            this.closeAll();
        }
    }

    public expand(id: string): void {
        const { root, modifiedTile } = expandTile(this._tree, id);
        this._tree = root;

        if (modifiedTile !== undefined) {
            this.tiles = [modifiedTile];
            this.joints = [];
        }
    }

    private reopen(name: string, args?: any, box?: Box, id?: string): void {
        const key = guid();

        const tilesToClose = this.tiles.filter(
            x => x.name === name && !boxesAreEqual(x.box, box)
        );

        tilesToClose.forEach(tileToClose => {
            this.close(tileToClose.key);
        });

        const tile = createTileData(key, name, args, box);

        // Если всего один тайл, то вместе с ним требуется обновить и соответствующий _tree,
        // так как весь массив tiles будет заменён на новый методом splice и потеряется его связь с _tree,
        // и станет невозможно автообновление поля box в _tree при обновлении соответствующего поля у tile.
        if (this.tiles.length === 1) {
            this._tree = tile;
        }

        const updatedTileIndex = id
            ? this.tiles.findIndex(x => x.key === id)
            : this.tiles.findIndex(x => x.name === name);

        this.tiles = id
            ? this.tiles.filter(x => x.key !== id)
            : this.tiles.filter(x => x.name !== name);

        this.tiles.splice(updatedTileIndex, 0, tile);

        const sides: (keyof Joint<TileData, ContainerData>)[] = [
            'right',
            'left',
        ];

        const { field, value }: { field: keyof TileData; value: string } =
            id !== undefined
                ? { field: 'key', value: id }
                : { field: 'name', value: name };

        this.joints.forEach(joint => {
            sides.forEach(side => {
                if ((joint[side] as TileData)[field] === value) {
                    (joint[side] as TileData) = tile;
                }
            });
        });

        for (const node of enumerateNodes(this._tree)) {
            if (isJoint(node)) {
                const currentJoint = node as Joint<TileData, ContainerData>;

                sides.forEach(side => {
                    if (
                        isLeaf(currentJoint[side]) &&
                        (currentJoint[side] as TileData)[field] === value
                    ) {
                        (currentJoint[side] as TileData) = tile;
                    }
                });
            } else if ((node as TileData)[field] === value) {
                (node as TileData).key = tile.key;
            }
        }
    }

    private restoreTile(name: string, args?: any, key: string = '') {
        key = key !== '' ? key : guid();

        let currentTile: TileData | null = null;
        if (this.currentTileKey !== '') {
            currentTile = this.tiles.find(r => r.key === this.currentTileKey)!;
        }

        const { root, modifiedTile, modifiedJoint } = insertToLargestTile(
            this._tree,
            { key, name, args },
            currentTile!
        );
        this._tree = root;
        this.tiles.push(modifiedTile!);

        if (modifiedJoint !== undefined) {
            this.joints.push(modifiedJoint);
        }

        if (this.tiles.length > 1) {
            const updatedKeys = updateSplitRatio(
                modifiedJoint!,
                modifiedJoint!.data!.ratio
            );
            this.fireResizeEventForKeys(updatedKeys);
        }

        return modifiedTile!.key;
    }

    private onResize(joint: Joint<TileData, ContainerData>, ratio: number) {
        this.lastSourceTile = null;
        this.lastTargetTile = null;

        const updatedKeys = updateSplitRatio(joint, ratio);
        this.fireResizeEventForKeys(updatedKeys);
    }

    private windowResized() {
        const tileKeys = this.tiles.map(({ key }) => key);
        this.fireResizeEventForKeys(tileKeys);
    }

    private fireResizeEventForKeys(keys: string[]) {
        keys.forEach(key => this.fireResizeEventByKey(key));
    }

    private fireResizeEventByKey(key: string) {
        const refs = this.$refs[key];

        if (Array.isArray(refs)) {
            refs.forEach((component: any) =>
                this.fireResizeEventForComponent(component)
            );
        } else {
            this.fireResizeEventForComponent(refs);
        }
    }

    private fireResizeEventForComponent(component: any) {
        if (component && component.resized) {
            component.resized();
        }
    }

    /**
     * Returns handler that prepares data for drag-n-drop operation.
     * The main task of this method is to transmit TileData into the
     * handler of preparation for drag-n-drop operation.
     * @param {TileData} tile The dragged tile data
     * @returns {(data: IDnDData) => void} Handler of data preparation
     * for drag-n-drop operation
     */
    private getPrepareDragData(tile: TileData): (data: IDnDData) => void {
        return (data: IDnDData) => {
            this.onPrepareDragData(tile, data);
            this.lastSourceTile = tile;
        };
    }
    /**
     * Handler that prepares data for drag-n-drop operation.
     * Handler set type and value of given drag-n-drop operation data
     * @param {TileData} tile The dragged tile data
     * @param {IDnDData} data The drag-n-drop operation data
     */
    private onPrepareDragData(tile: TileData, data: IDnDData): void {
        data.typeName = this.DragDataType;
        data.value = tile;
    }
    /**
     * Returns handler that processes drop event of drag-n-drop operation.
     * The main task of this method is to transmit TileData into the
     * handler of drop event.
     * @param {TileData} tile The dragged tile data
     * @returns {(data: IDnDData) => void} Handler of drop event
     */
    private getDropSourceDataHandler(
        sourceTile: TileData
    ): (evt: DragEvent) => void {
        return (evt: DragEvent) => {
            if (this.lastSourceTile && this.lastTargetTile) {
                this.onDropData(this.lastSourceTile, this.lastTargetTile, evt);
            }
            this.lastSourceTile = sourceTile;
        };
    }
    /**
     * Returns handler that processes drop event of drag-n-drop operation.
     * The main task of this method is to transmit TileData into the
     * handler of drop event.
     * @param {TileData} tile The dragged tile data
     * @returns {(data: IDnDData) => void} Handler of drop event
     */
    private getDropDataHandler(
        targetTile: TileData
    ): (data: IDnDData, evt: DragEvent) => void {
        return (data: IDnDData, evt: DragEvent) => {
            this.onDropData(data.value, targetTile, evt);
            if (targetTile) {
                this.lastTargetTile = targetTile;
            }
        };
    }
    /**
     * Handler that processes drop event of drag-n-drop operation.
     * Using for mosaic.
     * @param {TileData} droppedTile The data of the tile that was dragged
     * @param {TileData} targetTile The data of the tile that dragged data was dropped on
     */
    private onDropData(
        droppedTile: TileData,
        targetTile: TileData,
        evt: DragEvent
    ): void {
        if (this.dragStartMosaic2 && this.targetTileDOM2) {
            this.newAdjacentTileDataUpdating(
                this.targetTileDOM2 as HTMLElement
            );
        }

        this.droppedTileConst = null;

        this.droppedTileDOM!.className = 's-page s-panel';
        this.droppedTileDOM = null;

        this.targetTileDOM = null;
        this.targetTileDOM2 = null;
        this.position = null;
        this.positionEquelsCss = null;

        this.dragStartMosaic = false;
        this.dragStartMosaic2 = false;
        this.dragStartMosaic3 = false;

        this.lastSourceTile = null;
        this.lastTargetTile = null;
    }

    private mosaic(droppedTile: TileData, evt: DragEvent): void {
        if (!this.dragStartMosaic && droppedTile !== null) {
            this.droppedTileConst = droppedTile;
            this.dragStartMosaic = true;
        }

        this.targetTileDOM = (evt!.target! as HTMLElement).closest('.s-page.s-panel')!;
        if (this.droppedTileDOM === null) {
            this.droppedTileDOM = (evt!.srcElement! as HTMLElement).closest('.s-page.s-panel')!;
        }

        const viewport: DOMRect = this.targetTileDOM.getBoundingClientRect();
        const column: number =
            ((evt!.clientY - viewport.top) / viewport.height) * 100.0;
        const row: number =
            ((evt!.clientX - viewport.left) / viewport.width) * 100.0;

        if (row > 25 && column < 25 && row < 75) {
            this.position = Position.Top;
        } else if (column > 25 && row > 75 && column < 75) {
            this.position = Position.Right;
        } else if (row > 25 && column > 75 && row < 75) {
            this.position = Position.Bottom;
        } else if (column > 25 && row < 25 && column < 75) {
            this.position = Position.Left;
        }

        if (!this.droppedTileDOM.isEqualNode(this.targetTileDOM)) {
            this.droppedTileDOM.className = `s-page s-panel drop-target drop-target-hover`;

            if (!this.dragStartMosaic2) {
                this.adjacentTileStretching(this.droppedTileConst!);
                this.dragStartMosaic2 = true;
            }

            // In order to the marker immediately applies normally.
            if (this.dragStartMosaic3) {
                this.newPositionMarking(this.position!);
            }
            this.dragStartMosaic3 = true;
        }
    }

    /**
     * Checking CSS properties.
     * @param {Box} tileBox Box data of tile
     * @param {HTMLElement.style} targetTileDOM DOM of target (new adjacent) tile CSS styles
     */
    private checkCSSEquality(tileBox: any, targetTileDOM: any) {
        return (tileBox.top as number).toFixed(3) === Number(
                                                (targetTileDOM.top as string)
                                                .replace('%', '')
                                             ).toFixed(3) &&
               (tileBox.bottom as number).toFixed(3) === Number(
                                                (targetTileDOM.bottom as string)
                                                .replace('%', '')
                                        ).toFixed(3) &&
               (tileBox.left as number).toFixed(3) === Number(
                                                (targetTileDOM.left as string)
                                                .replace('%', '')
                                      ).toFixed(3) &&
               (tileBox.right as number).toFixed(3) === Number(
                                                (targetTileDOM.right as string)
                                                .replace('%', '')
                                       ).toFixed(3);
    }

    /**
     * Updating new adjacent tile data.
     * @param {HTMLElement} droppedTileDOM DOM of dropped tile
     * @param {HTMLElement} targetTileDOM DOM of target (new adjacent) tile
     */
    private newAdjacentTileDataUpdating(targetTileDOM: HTMLElement): void {
        this.tiles.forEach(tile => {
            let direction: Direction | null;

            if (this.checkCSSEquality(tile.box, targetTileDOM.style)) {
                if (this.positionEquelsCss === Position.Top) {
                    tile.box = {
                        ...tile.box!,
                        top: 100 - this.droppedTileConst!.box.bottom,
                    };
                    direction = Direction.Vertical;
                } else if (this.positionEquelsCss === Position.Right) {
                    tile.box = {
                        ...tile.box!,
                        right: 100 - this.droppedTileConst!.box.left,
                    };
                    direction = Direction.Horizontal;
                } else if (this.positionEquelsCss === Position.Bottom) {
                    tile.box = {
                        ...tile.box!,
                        bottom: 100 - this.droppedTileConst!.box.top,
                    };
                    direction = Direction.Vertical;
                } else if (this.positionEquelsCss === Position.Left) {
                    tile.box = {
                        ...tile.box!,
                        left: 100 - this.droppedTileConst!.box.right,
                    };
                    direction = Direction.Horizontal;
                }

                if (this.tiles.length > 2) {
                    const modifiedJoint = insertToTile(
                        this._tree!,
                        tile,
                        this.droppedTileConst!,
                        true,
                        direction!
                    );

                    this.joints.push(modifiedJoint);
                    this.onResize(modifiedJoint, modifiedJoint.data!.ratio);
                } else {
                    const rootJoint = createJoint(
                        this.droppedTileConst!,
                        tile,
                        true,
                        direction!
                    );

                    this._tree = rootJoint;
                    this.joints.push(rootJoint);
                    this.onResize(rootJoint, rootJoint.data!.ratio);
                }
            }
        });
    }

    /**
     * Block for stretching adjacent tile.
     * @param {TileData} droppedTile The dragged tile data
     */
    private adjacentTileStretching(droppedTile: TileData): void {
        let adjacentTilesOutsideJointLeft = true;
        let adjacentTilesOutsideJointTop = true;
        let adjacentTilesOutsideJointRight = true;
        let adjacentTilesOutsideJointBottom = true;
        let {
            mainSide,
            parallelSide,
            borderSide1,
            borderSide2,
        }: {
            mainSide: keyof Box | null;
            parallelSide: keyof Box | null;
            borderSide1: keyof Box | null;
            borderSide2: keyof Box | null;
        } = {
            mainSide: null,
            parallelSide: null,
            borderSide1: null,
            borderSide2: null,
        };
        this.joints.forEach(joint => {
            if (joint.left === droppedTile) {
                if (joint.data!.direction === Direction.Horizontal) {
                    this.tiles.forEach(tile => {
                        if (joint.right === tile) {
                            tile.box = {
                                ...tile.box,
                                left: droppedTile.box.right - tile.box.left,
                            };
                            joint.right.box = tile.box;
                            adjacentTilesOutsideJointLeft = false;
                        }
                    });
                    if (adjacentTilesOutsideJointLeft) {
                        mainSide = 'left';
                        parallelSide = 'right';
                        borderSide1 = 'top';
                        borderSide2 = 'bottom';
                    }
                } else if (joint.data!.direction === Direction.Vertical) {
                    this.tiles.forEach(tile => {
                        if (joint.right === tile) {
                            tile.box = {
                                ...tile.box,
                                top: droppedTile.box.bottom - tile.box.top,
                            };
                            adjacentTilesOutsideJointTop = false;
                        }
                    });
                    if (adjacentTilesOutsideJointTop) {
                        mainSide = 'top';
                        parallelSide = 'bottom';
                        borderSide1 = 'right';
                        borderSide2 = 'left';
                    }
                }
            }
            if (joint.right === droppedTile) {
                if (joint.data!.direction === Direction.Horizontal) {
                    this.tiles.forEach(tile => {
                        if (joint.left === tile) {
                            tile.box = {
                                ...tile.box!,
                                right: droppedTile.box.left - tile.box.right,
                            };
                            adjacentTilesOutsideJointRight = false;
                        }
                    });
                    if (adjacentTilesOutsideJointRight) {
                        mainSide = 'right';
                        parallelSide = 'left';
                        borderSide1 = 'top';
                        borderSide2 = 'bottom';
                    }
                } else if (joint.data!.direction === Direction.Vertical) {
                    this.tiles.forEach(tile => {
                        if (joint.left === tile) {
                            tile.box = {
                                ...tile.box,
                                bottom: droppedTile.box.top - tile.box.bottom,
                            };
                            adjacentTilesOutsideJointBottom = false;
                        }
                    });
                    if (adjacentTilesOutsideJointBottom) {
                        mainSide = 'bottom';
                        parallelSide = 'top';
                        borderSide1 = 'right';
                        borderSide2 = 'left';
                    }
                }
            }
        });

        if (
            mainSide !== null ||
            parallelSide !== null ||
            borderSide1 !== null ||
            borderSide2 !== null
        ) {
            this.tiles.forEach(tile => {
                if (
                    tile !== droppedTile &&
                    tile.box[borderSide1!] >= droppedTile.box[borderSide1!] &&
                    tile.box[borderSide2!] >= droppedTile.box[borderSide2!] &&
                    tile.box[mainSide!] + droppedTile.box[parallelSide!] === 100
                ) {
                    const valueSide =
                        droppedTile.box[parallelSide!] - tile.box[mainSide!];

                    tile.box = {
                        ...tile.box!,
                        [mainSide!]: valueSide,
                    };

                    for (const node of enumerateNodes(this._tree)) {
                        if (isJoint(node)) {
                            const currentJoint = node as Joint<
                                TileData,
                                ContainerData
                            >;

                            if (
                                (isLeaf(currentJoint.left) &&
                                    (currentJoint.left as TileData).key ===
                                        tile.key) ||
                                (isLeaf(currentJoint.right) &&
                                    (currentJoint.right as TileData).key ===
                                        tile.key)
                            ) {
                                currentJoint.data!.boundingBox = {
                                    ...currentJoint.data!.boundingBox,
                                    [mainSide!]: valueSide,
                                };
                                currentJoint.data!.ratio =
                                    tile.box[parallelSide!] !== 0
                                        ? tile.box[parallelSide!] * 0.01
                                        : 0.5;
                            }
                        }
                    }
                }
            });
        }

        const { root, modifiedTile, modifiedJoint } = deleteTile(
            this._tree,
            droppedTile.key
        );

        this._tree = root;

        if (modifiedJoint !== undefined) {
            this.joints = this.joints.filter(x => x !== modifiedJoint);
        }
    }

    /**
     * Block for marker of new possible place of tile.
     * @param {string} pos The possible position of tile
     */
    private newPositionMarking(pos: string): void {
        const boxSides: (keyof Box)[] = ['top', 'right', 'bottom', 'left'];
        boxSides.forEach(side => {
            this.droppedTileConst!.box[side] = parseFloat(
                (this.targetTileDOM as HTMLElement).style[side]
            );
        });

        const droppedTileTop = this.droppedTileConst!.box!.top;
        const droppedTileRight = this.droppedTileConst!.box!.right;
        const droppedTileBottom = this.droppedTileConst!.box!.bottom;
        const droppedTileLeft = this.droppedTileConst!.box!.left;

        switch (pos) {
            case Position.Top:
                const bottom: number =
                    (100 - droppedTileTop) / 2 + droppedTileBottom / 2;
                this.droppedTileConst!.box.bottom = bottom;
                break;
            case Position.Right:
                const left: number =
                    (100 - droppedTileRight) / 2 + droppedTileLeft / 2;
                this.droppedTileConst!.box.left = left;
                break;
            case Position.Bottom:
                const top: number =
                    (100 - droppedTileBottom) / 2 + droppedTileTop / 2;
                this.droppedTileConst!.box.top = top;
                break;
            case Position.Left:
                const right: number =
                    (100 - droppedTileLeft) / 2 + droppedTileRight / 2;
                this.droppedTileConst!.box.right = right;
                break;
        }
        this.positionEquelsCss = this.position;

        this.targetTileDOM2 = this.targetTileDOM;
    }
}
